import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { EvolutionChart } from "@/components/EvolutionChart";
import { UsageMeter } from "@/components/UsageMeter";
import { EmptyState } from "@/components/EmptyState";
import { Avatar } from "@/components/Avatar";
import { SkeletonCard } from "@/components/Skeletons";
import { ReferralCard } from "@/components/ReferralCard";
import { useAuth } from "@/context/AuthContext";
import { relativeDate } from "@/lib/format";
import client from "@/lib/api";

export default function Overview() {
    const { user } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        client.get("/dashboard").then(({ data }) => setData(data)).catch(() => setData(false));
    }, []);

    const first = user?.name?.split(" ")[0] || "";

    if (data === null) {
        return (
            <>
                <PageHeader title={`Hola, ${first}`} />
                <div className="grid gap-4 sm:grid-cols-3">
                    <SkeletonCard /><SkeletonCard /><SkeletonCard />
                </div>
            </>
        );
    }

    const noData = data && data.kpis.analyses.total === 0;

    return (
        <>
            <PageHeader
                title={`Hola, ${first}`}
                subtitle="Este es el resumen de tu piel, tu neceser y tu rutina."
                action={
                    <Button asChild data-testid="overview-new-analysis">
                        <Link to="/app/analisis/nuevo">Analizar mi piel</Link>
                    </Button>
                }
            />

            {noData ? (
                <div className="space-y-8">
                    <EmptyState
                        testId="overview-empty"
                        title="Aún no sabemos nada de tu piel"
                        text="Empieza por un análisis rápido: unas preguntas y, si quieres, un par de fotos. Es la base de todo lo demás."
                        action={
                            <Button asChild>
                                <Link to="/app/analisis/nuevo">Hacer mi primer análisis</Link>
                            </Button>
                        }
                    />
                    <ReferralCard />
                </div>
            ) : (
                <div className="space-y-8">
                    <div className="grid gap-4 sm:grid-cols-3">
                        <StatCard testId="kpi-analyses" label="Análisis de piel" value={data.kpis.analyses.total}
                            delta={data.kpis.analyses.this - data.kpis.analyses.prev} />
                        <StatCard testId="kpi-products" label="Productos en tu neceser" value={data.kpis.products.total}
                            delta={data.kpis.products.this - data.kpis.products.prev} />
                        <StatCard testId="kpi-reports" label="Informes generados" value={data.kpis.reports.total}
                            delta={data.kpis.reports.this - data.kpis.reports.prev} />
                    </div>

                    {data.evolution.length > 0 && <EvolutionChart data={data.evolution} />}

                    {data.limits && (
                        <div>
                            <h3 className="mb-3 text-[16px] font-bold tracking-tight text-ink">Tu plan gratuito</h3>
                            <div className="grid gap-3 sm:grid-cols-3">
                                <UsageMeter label="Análisis" used={data.limits.analyses.used} max={data.limits.analyses.max} to="/app/cuenta" />
                                <UsageMeter label="Productos" used={data.limits.products.used} max={data.limits.products.max} to="/app/cuenta" />
                                <UsageMeter label="Informes" used={data.limits.reports.used} max={data.limits.reports.max} to="/app/cuenta" />
                            </div>
                        </div>
                    )}

                    <div className="grid gap-6 lg:grid-cols-2">
                        <div className="rounded-lg border border-line bg-card p-5">
                            <div className="mb-4 flex items-center justify-between">
                                <h3 className="text-[16px] font-bold tracking-tight text-ink">Últimos productos</h3>
                                <Link to="/app/neceser" className="text-[13px] text-terracotta hover:underline">Ver neceser</Link>
                            </div>
                            {data.recent_products.length === 0 ? (
                                <p className="text-[14px] text-subtle">Todavía no has añadido productos.</p>
                            ) : (
                                <ul className="space-y-3">
                                    {data.recent_products.map((p) => (
                                        <li key={p.id} className="flex items-center gap-3">
                                            <Avatar name={p.brand || p.name} size={36} seed={p.id} />
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-[15px] font-medium text-ink">{p.name}</p>
                                                <p className="text-[13px] text-subtle">{p.category_label}</p>
                                            </div>
                                            <span className="text-[12px] text-subtle">{relativeDate(p.created_at)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="rounded-lg border border-line bg-card p-5">
                            <h3 className="text-[16px] font-bold tracking-tight text-ink">Tu piel ahora</h3>
                            {data.last_analysis ? (
                                <>
                                    <p className="mt-2 text-[14px] leading-[1.6] text-subtle">{data.last_analysis.summary}</p>
                                    <div className="mt-4 flex flex-wrap gap-3">
                                        <Button asChild variant="outline" size="sm">
                                            <Link to="/app/resultado">Ver mi resultado</Link>
                                        </Button>
                                        <Button asChild size="sm">
                                            <Link to="/app/analisis/nuevo">Volver a analizarme</Link>
                                        </Button>
                                    </div>
                                    <p className="mt-4 text-[13px] text-subtle">
                                        Te recomendamos repetir el análisis cada {data.reminder_days} días.
                                    </p>
                                </>
                            ) : (
                                <p className="mt-2 text-[14px] text-subtle">Haz tu primer análisis para verlo aquí.</p>
                            )}
                        </div>
                    </div>

                    <ReferralCard />
                </div>
            )}
        </>
    );
}
